import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as Actions from '../../../redux/actions/index';
import { Modal } from 'react-bootstrap';
import { FocusTrap } from 'focus-trap-react';

import FloatingInput from 'components/elements/atoms/FloatingInput/FloatingInput';
import RectangularButton from 'components/elements/molecules/RectangularButton/RectangularButton';
import ActionLink from 'components/elements/molecules/ActionLink/ActionLink';
import { Icon, ICONS } from '../../Icons';
import ToolTip from 'components/elements/atoms/ToolTip/ToolTip';

import './ManageTopic.scss';

const STEPS = ['Basics', 'Dates', 'Confirm'];

function ManageTopic({ courseId, topic = {}, onClose, actions }) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState(topic.name || '');
  const [description, setDescription] = useState(topic.description || '');
  const [lectureDates, setLectureDates] = useState(
    () =>
      topic.lectureDates || [topic.dueDate || moment().format('YYYY-MM-DD')],
  );
  const [dueDate, setDueDate] = useState(topic.dueDate || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [createdTopicName, setCreatedTopicName] = useState(topic.name || '');

  const isCreateMode = !topic._id;
  const modalContentRef = useRef(null);

  const moveFocusOffInput = (event) => {
    const interactiveElement = event.target.closest(
      'input, textarea, button, a, select, label',
    );

    if (interactiveElement) {
      return;
    }

    window.requestAnimationFrame(() => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      modalContentRef.current?.focus();
    });
  };

  const clearError = () => {
    if (error) {
      setError('');
    }
  };

  const updateLectureDate = (index, value) => {
    clearError();
    setLectureDates((current) =>
      current.map((date, currentIndex) =>
        currentIndex === index ? value : date,
      ),
    );
  };

  const addLectureDate = () => {
    clearError();
    setLectureDates((current) => [...current, '']);
  };

  const removeLectureDate = (index) => {
    clearError();
    setLectureDates((current) =>
      current.filter((_, currentIndex) => currentIndex !== index),
    );
  };

  const closeDatePickerAfterSelection = (event, index) => {
    updateLectureDate(index, event.target.value);

    window.requestAnimationFrame(() => {
      event.target.blur();
    });
  };

  const getStepError = () => {
    if (step === 0) {
      if (!name.trim()) {
        return 'Please enter a topic title.';
      }
    }

    if (step === 1) {
      if (!lectureDates.some(Boolean) && !dueDate) {
        return 'Please add at least one lecture date or due date.';
      }
      if (lectureDates.some(Boolean) && lectureDates.some((date) => !date)) {
        return 'Please enter a date for every lecture date row.';
      }
    }

    return '';
  };

  const isCurrentStepValid = () => !getStepError();

  const getEditError = () => {
    if (!name.trim()) return 'Please enter a topic title.';
    if (!lectureDates.some(Boolean) && !dueDate)
      return 'Please add at least one lecture date or due date.';
    if (lectureDates.some(Boolean) && lectureDates.some((date) => !date))
      return 'Please enter a date for every lecture date row.';
    return '';
  };

  const saveEdit = async () => {
    const err = getEditError();
    if (err) {
      setError(err);
      return;
    }
    setSaving(true);
    try {
      await actions.updateTopic(
        courseId,
        topic._id,
        name.trim(),
        dueDate || null,
        lectureDates,
        description,
      );
      onClose();
    } catch (requestError) {
      setError(
        requestError?.message || 'Error updating topic. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  const saveTopic = async () => {
    const stepError = getStepError();

    if (stepError) {
      setError(stepError);
      return;
    }

    setSaving(true);

    try {
      let topicResult;
      if (topic._id) {
        topicResult = await actions.updateTopic(
          courseId,
          topic._id,
          name.trim(),
          dueDate || null,
          lectureDates,
          description,
        );
      } else {
        topicResult = await actions.createTopic(
          courseId,
          name.trim(),
          dueDate || null,
          lectureDates,
          description,
        );
      }

      const savedTopicName =
        name.trim() || topicResult?.name || topic.name || 'Topic';
      setCreatedTopicName(savedTopicName);

      setStep(2);
    } catch (requestError) {
      setError(
        requestError?.message ||
          `Error occurred while ${
            isCreateMode ? 'adding' : 'updating'
          } topic. Please try again.`,
      );
    } finally {
      setSaving(false);
    }
  };

  const goToNextStep = () => {
    const stepError = getStepError();

    if (stepError) {
      setError(stepError);
      return;
    }

    if (step === 1) {
      saveTopic();
      return;
    }

    setStep((current) => current + 1);
  };

  const goBack = () => {
    clearError();

    if (step === 0) {
      return;
    }

    if (step === 2) {
      onClose();
      return;
    }

    setStep((current) => current - 1);
  };

  const formatLectureDatesConfirm = (dates) => {
    const validDates = dates.filter(Boolean).sort();
    if (!validDates.length) return null;

    if (validDates.length === 1) {
      return `Lecture date: ${moment(validDates[0]).format('MMMM D')}`;
    }

    return `Lecture dates: ${moment(validDates[0]).format('MMMM D')} to ${moment(validDates[validDates.length - 1]).format('MMMM D')}`;
  };

  const getSuggestionDateLabel = (date) => {
    if (!date) return 'Sunday';

    const lectureDate = moment(date);
    const previousSunday = lectureDate.clone().day(0);

    if (previousSunday.isSameOrAfter(lectureDate, 'day')) {
      previousSunday.subtract(7, 'days');
    }

    return previousSunday.format('dddd, MMMM D');
  };

  const renderHeader = () => (
    <div className="header-row">
      <button
        type="button"
        onClick={onClose}
        className="btn-av-container close btn-close"
        aria-label="Close"
      >
        <Icon icon={ICONS.CLOSE} className="mr-0" />
      </button>
    </div>
  );

  const renderSteps = () => (
    <div className="steps-container">
      {STEPS.map((stepName, index) => {
        const isCompleted = index < step;
        const isActive = index === step;

        return (
          <div
            key={stepName}
            className={classNames('step-item', {
              completed: isCompleted,
              active: isActive,
            })}
          >
            <div className="step-line" />
            <div className="step-circle">
              {isCompleted ? (
                <Icon icon={ICONS.VERIFIED} className="step-check" />
              ) : (
                index + 1
              )}
            </div>
            <div className="step-label">{stepName}</div>
          </div>
        );
      })}
    </div>
  );

  const renderDescriptionInput = () => (
    <>
      <div className="textarea-label">
        Description <span>(optional)</span>
      </div>
      <textarea
        className="topic-description"
        placeholder="e.g. An introduction to the concept of economic value, including use value, exchange value, and the role of scarcity."
        value={description}
        onChange={(event) => {
          clearError();
          setDescription(event.target.value);
        }}
        rows={5}
      />
    </>
  );

  const renderTopicNameInput = () => (
    <FloatingInput
      autoFocus
      value={name}
      labelName="Topic title"
      onChange={(event) => {
        clearError();
        setName(event.target.value);
      }}
      required
    />
  );

  const renderTopicStep = () => (
    <div className="step-panel">
      <h2>What is your topic?</h2>
      <p className="mb-5">
        This information will help us find relevant news articles.
      </p>
      {renderTopicNameInput()}
      <div className="description-section">{renderDescriptionInput()}</div>
      {step === 0 && error ? <div className="step-error">{error}</div> : null}
    </div>
  );

  const renderDueDateInput = () => (
    <FloatingInput
      value={dueDate ? moment(dueDate).format('YYYY-MM-DD') : ''}
      labelName="Due date"
      type="date"
      placeholder="mm/dd/yyyy"
      onChange={(event) => {
        clearError();
        setDueDate(event.target.value);
      }}
      min={isCreateMode ? moment().format('YYYY-MM-DD') : undefined}
    />
  );

  const renderLectureDateRow = (dateValue, index) => (
    <div key={`${index}-${dateValue || 'new'}`} className="lecture-date-row">
      <div className="lecture-date-input-wrap">
        <FloatingInput
          value={dateValue ? moment(dateValue).format('YYYY-MM-DD') : ''}
          type="date"
          labelName={index === 0 ? 'Lecture date(s)' : ''}
          placeholder="mm/dd/yyyy"
          onChange={(event) => closeDatePickerAfterSelection(event, index)}
          min={isCreateMode ? moment().format('YYYY-MM-DD') : undefined}
        />
      </div>
      <div className="lecture-date-remove-column">
        {index > 0 ? (
          <RectangularButton
            type="button"
            className="red remove-lecture-date"
            onClick={() => removeLectureDate(index)}
            aria-label="Remove lecture date"
          >
            <Icon icon={ICONS.CLOSE} className="av-red" />
          </RectangularButton>
        ) : null}
      </div>
    </div>
  );

  const renderLectureDatesInputs = () => (
    <>
      <div className="lecture-dates-group">
        {lectureDates.map((dateValue, index) =>
          renderLectureDateRow(dateValue, index),
        )}
      </div>
      <ActionLink
        label="+ Add another lecture date"
        linkClassName="add-lecture-date mb-5"
        onClick={addLectureDate}
      />
    </>
  );

  const renderLectureDateStep = () => (
    <div className="step-panel">
      <h2 className="mb-5">Set up your dates</h2>
      {name.trim() && (
        <p className="mb-4 mt-0">
          <span className="font-weight-600">Topic title:</span> {name.trim()}
        </p>
      )}
      <div className="dates-two-col">
        <div className="dates-col">{renderLectureDatesInputs()}</div>
        <div className="dates-col">{renderDueDateInput()}</div>
      </div>
    </div>
  );

  const renderConfirmStep = () => {
    const lectureDatesSummary = formatLectureDatesConfirm(lectureDates);
    const formattedDueDate = dueDate
      ? `Due date: ${moment(dueDate).format('MMMM D')}`
      : null;
    const suggestionDate = getSuggestionDateLabel(
      lectureDates.filter(Boolean).sort()[0] || '',
    );

    return (
      <div className="step-panel confirm-panel">
        <h2 className="mb-5">
          You {isCreateMode ? 'created' : 'updated'} the topic{' '}
          {createdTopicName || name.trim() || 'Topic'}
        </h2>
        <div className="confirm-dates">
          {lectureDatesSummary && (
            <p className="confirm-date-line">{lectureDatesSummary}</p>
          )}
          {formattedDueDate && (
            <p className="confirm-date-line">{formattedDueDate}</p>
          )}
        </div>
        <div className="confirm-list">
          <div className="confirm-item">
            <span className="confirm-icon">
              <Icon icon={ICONS.VERIFIED} />
            </span>
            <span>
              On {suggestionDate}, we'll email you suggested articles for the
              week. You then review and publish.
            </span>
          </div>
          <div className="confirm-item">
            <span className="confirm-icon">
              <Icon icon={ICONS.VERIFIED} />
            </span>
            <span>
              You can now assign reading, research, and writing assignments.
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderBody = () => {
    switch (step) {
      case 0:
        return renderTopicStep();
      case 1:
        return renderLectureDateStep();
      case 2:
        return renderConfirmStep();
      default:
        return null;
    }
  };

  const renderEditView = () => (
    <div>
      <div className="edit-modal-header">
        <div className="edit-modal-header-text">
          <h2 className="edit-modal-title">Edit topic</h2>
          <p className="edit-modal-subtitle">{topic.name}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="btn-av-container close btn-close"
          aria-label="Close"
        >
          <Icon icon={ICONS.CLOSE} className="mr-0" />
        </button>
      </div>
      <div className="edit-form-divider" />
      <div className="edit-form-body">
        {renderTopicNameInput()}
        {renderDescriptionInput()}
        <div className="dates-row">
          <div className="lecture-dates-column">
            {renderLectureDatesInputs()}
          </div>
          {renderDueDateInput()}
        </div>
        {error && <div className="step-error">{error}</div>}
      </div>
      <div className="edit-form-divider" />
      <div className="edit-footer">
        <RectangularButton lightButton onClick={onClose}>
          Cancel
        </RectangularButton>
        <RectangularButton disabled={saving} onClick={saveEdit}>
          Save changes
        </RectangularButton>
      </div>
    </div>
  );

  return (
    <Modal
      show
      className={classNames('manage-topic-modal', {
        'manage-topic-modal--edit': !isCreateMode,
      })}
    >
      <FocusTrap>
        <div>
          <Modal.Body>
            {!isCreateMode ? (
              renderEditView()
            ) : (
              <>
                {renderHeader()}
                <div
                  className="wizard-shell"
                  ref={modalContentRef}
                  tabIndex={-1}
                  onMouseDown={moveFocusOffInput}
                >
                  {renderSteps()}
                  <div className="wizard-body">{renderBody()}</div>
                  <div className="wizard-footer">
                    <RectangularButton
                      onClick={goBack}
                      lightButton
                      className={classNames('back-button', {
                        invisible: step === 0 || step === 2,
                      })}
                    >
                      Back
                    </RectangularButton>
                    {error ? (
                      <div
                        className="step-error mt-0"
                        style={{ textAlign: 'center' }}
                      >
                        {error}
                      </div>
                    ) : null}
                    <ToolTip
                      toolTipText="You need at least one lecture date or due date."
                      toolTipPosition="top"
                      hide={
                        !(step === 1 && !lectureDates.some(Boolean) && !dueDate)
                      }
                    >
                      <RectangularButton
                        className="next-button"
                        disabled={!isCurrentStepValid() || saving}
                        onClick={step === 2 ? onClose : goToNextStep}
                      >
                        {step === 1
                          ? 'Create topic'
                          : step === 2
                            ? 'Done'
                            : 'Continue'}
                      </RectangularButton>
                    </ToolTip>
                  </div>
                </div>
              </>
            )}
          </Modal.Body>
        </div>
      </FocusTrap>
    </Modal>
  );
}

function mapStateToProps(state) {
  return {
    courses: state.classRoom.courses || [],
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Actions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ManageTopic);
